import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Modal,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import ListAltIcon from "@mui/icons-material/ListAlt";
import PostAddIcon from "@mui/icons-material/PostAdd";
import colors from "../../theme/colors";
import { AppProvider } from "../../ContextApi/AppProvider";
import {
  Compra,
  CompraItem,
  FornecedorApi,
  ProdutosCompraDTO,
  initialCompra,
  initialCompraItem,
} from "../Shared/Interfaces/Compra";
import IProduto from "../Shared/Interfaces/Produto";
import { ComprasService } from "../../Services/ComprasService";
import { ProdutosServices } from "../../Services/ProdutosService";
import { formatToBRL } from "../../Utils/utils";
import { useNavigate } from "react-router-dom";
import ModalCadastrarProduto from "./ModalCadastrarProduto";

const comprasService = new ComprasService();
const produtosService = new ProdutosServices();

const getProductLabel = (produto?: IProduto | null) => {
  if (!produto) return "";
  return `${produto.descricao} - ${produto.medida} - ${produto.marca}`;
};

const extractErrorMessage = (error: any): string => {
  if (!error) return "Erro desconhecido";
  
  // Se for uma string, retorna diretamente
  if (typeof error === "string") return error;
  
  // Se for um objeto com propriedades de erro do ASP.NET Core
  if (error.response?.data) {
    const errorData = error.response.data;
    
    // Se for um objeto com title (padrão ASP.NET Core)
    if (errorData.title) return errorData.title;
    
    // Se for um objeto com message
    if (errorData.message) return errorData.message;
    
    // Se for um objeto com errors (validação)
    if (errorData.errors && typeof errorData.errors === "object") {
      const errorMessages = Object.values(errorData.errors).flat();
      if (errorMessages.length > 0) {
        return Array.isArray(errorMessages[0]) 
          ? errorMessages[0].join(", ") 
          : String(errorMessages[0]);
      }
    }
    
    // Se for um array de strings
    if (Array.isArray(errorData)) {
      return errorData.join(", ");
    }
    
    // Se for um objeto, tenta converter para string
    if (typeof errorData === "object") {
      return JSON.stringify(errorData);
    }
    
    return String(errorData);
  }
  
  // Se tiver message direto
  if (error.message) return error.message;
  
  return "Erro desconhecido";
};

const styleModalFornecedor = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95vw", sm: 420 },
  maxWidth: "90vw",
  maxHeight: "90vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: { xs: 2, sm: 3 },
  borderRadius: 1,
};

/** `BuscarFornecedores` retorna `nome`; o cadastro usa `descricao`. Unificamos em `descricao`. */
const normalizeFornecedorApiItem = (raw: {
  id: number;
  descricao?: string;
  nome?: string;
  cnpj?: string;
}): FornecedorApi => ({
  id: raw.id,
  descricao: (raw.descricao ?? raw.nome ?? "").trim(),
  cnpj: (raw.cnpj ?? "").trim(),
});

export default function Compras() {
  const contextApi = useContext(AppProvider);
  const navigate = useNavigate();

  const rawUser = sessionStorage.getItem("User");
  const user = rawUser ? JSON.parse(rawUser) : null;
  const isAdm = user?.papel === "Administrador";

  const [compraForm, setCompraForm] = useState<Compra>({
    ...initialCompra,
  });
  const [compraItens, setCompraItens] = useState<CompraItem[]>([]);
  const [produtos, setProdutos] = useState<IProduto[]>([]);
  const [produtoSelecionado, setProdutoSelecionado] = useState<IProduto | null>(
    null
  );
  const [quantidadeItem, setQuantidadeItem] = useState<number>(1);
  const [valorUnitarioItem, setValorUnitarioItem] = useState<number>(0);
  const [compras, setCompras] = useState<Compra[]>([]);
  const [loadingSalvar, setLoadingSalvar] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [fornecedorNome, setFornecedorNome] = useState<string>("");
  const [fornecedorCnpj, setFornecedorCnpj] = useState<string>("");
  const [fornecedoresCatalogo, setFornecedoresCatalogo] = useState<
    FornecedorApi[]
  >([]);
  const [openModalFornecedor, setOpenModalFornecedor] = useState(false);
  const [modalFornecedorDescricao, setModalFornecedorDescricao] =
    useState("");
  const [modalFornecedorCnpj, setModalFornecedorCnpj] = useState("");
  /** null = cadastro novo; número = edição desse id */
  const [modalFornecedorEdicaoId, setModalFornecedorEdicaoId] = useState<
    number | null
  >(null);
  const [loadingFornecedor, setLoadingFornecedor] = useState(false);
  const [openModalCadastrarProduto, setOpenModalCadastrarProduto] =
    useState(false);

  const [openModalItens, setOpenModalItens] = useState(false);
  const [modalCompra, setModalCompra] = useState<Compra | null>(null);
  const [modalItens, setModalItens] = useState<CompraItem[]>([]);
  /** 0 = nova compra, 1 = histórico */
  const [tabCompras, setTabCompras] = useState(0);

  const totalCompraAtual = useMemo(
    () =>
      compraItens.reduce(
        (acc, item) => acc + item.quantidade * item.valorUnitario,
        0
      ),
    [compraItens]
  );

  const fornecedorOptions = useMemo(() => {
    const list = [...fornecedoresCatalogo];
    const id = compraForm.fornecedor?.id;
    const nome = compraForm.fornecedor?.nome || fornecedorNome;
    const cnpj = compraForm.fornecedor?.cnpj || fornecedorCnpj;
    if (id && nome && !list.some((f) => f.id === id)) {
      list.push({ id, descricao: nome, cnpj: cnpj || "" });
    }
    return list;
  }, [
    fornecedoresCatalogo,
    compraForm.fornecedor?.id,
    compraForm.fornecedor?.nome,
    compraForm.fornecedor?.cnpj,
    fornecedorNome,
    fornecedorCnpj,
  ]);

  const fornecedorSelecionadoCombo = useMemo((): FornecedorApi | null => {
    const id = compraForm.fornecedor?.id;
    if (!id) return null;
    return fornecedorOptions.find((f) => f.id === id) || null;
  }, [fornecedorOptions, compraForm.fornecedor?.id]);

  useEffect(() => {
    if (!isAdm) {
      contextApi.showMessage(
        "Acesso permitido somente para administradores.",
        true,
        "warning"
      );
      navigate("/app/estoque");
      return;
    }
    carregarCompras();
    carregarProdutos();
    carregarFornecedores();
  }, [isAdm]);

  useEffect(() => {
    setCompraForm((prev) => ({ ...prev, valorTotal: totalCompraAtual }));
  }, [totalCompraAtual]);

  const carregarCompras = async () => {
    try {
      const response = await comprasService.listarCompras();
      // Garantir que sempre seja um array
      const comprasData = response.data;
      if (Array.isArray(comprasData)) {
        setCompras(comprasData);
      } else if (comprasData && Array.isArray(comprasData.data)) {
        setCompras(comprasData.data);
      } else {
        setCompras([]);
      }
    } catch (error: any) {
      contextApi.showMessage(
        extractErrorMessage(error) || "Erro ao carregar compras",
        true,
        "error"
      );
      setCompras([]);
    }
  };

  const carregarProdutos = async (): Promise<IProduto[]> => {
    try {
      const response = await produtosService.getAll("/api/Produtos/Buscar");
      const lista = response.data || [];
      setProdutos(lista);
      return lista;
    } catch (error: any) {
      contextApi.showMessage(
        extractErrorMessage(error) || "Erro ao buscar produtos",
        true,
        "error"
      );
      return [];
    }
  };

  const handleProdutoCadastradoNoEstoque = async (criado: IProduto) => {
    const lista = await carregarProdutos();
    const found =
      (criado.id > 0 && lista.find((p) => p.id === criado.id)) ||
      lista.find(
        (p) =>
          p.descricao === criado.descricao &&
          p.medida === criado.medida &&
          p.marca === criado.marca
      );
    if (found) {
      setProdutoSelecionado(found);
      setValorUnitarioItem(found.valor || 0);
    }
  };

  const carregarFornecedores = async (): Promise<FornecedorApi[]> => {
    try {
      const response = await comprasService.buscarFornecedores();
      const data = response.data;
      let arr: FornecedorApi[] = [];
      if (Array.isArray(data)) {
        arr = data.map((item: { id: number; descricao?: string; nome?: string; cnpj?: string }) =>
          normalizeFornecedorApiItem(item)
        );
      } else if (data && Array.isArray((data as { data?: unknown[] }).data)) {
        arr = (data as { data: { id: number; descricao?: string; nome?: string; cnpj?: string }[] }).data.map(
          (item) => normalizeFornecedorApiItem(item)
        );
      }
      setFornecedoresCatalogo(arr);
      return arr;
    } catch (error: any) {
      contextApi.showMessage(
        extractErrorMessage(error) || "Erro ao carregar fornecedores",
        true,
        "error"
      );
      setFornecedoresCatalogo([]);
      return [];
    }
  };

  const aplicarFornecedorSelecionado = (value: FornecedorApi | null) => {
    if (!value) {
      setFornecedorNome("");
      setFornecedorCnpj("");
      setCompraForm((prev) => ({
        ...prev,
        fornecedor: { id: 0, nome: "", cnpj: "" },
      }));
      return;
    }
    setFornecedorNome(value.descricao);
    setFornecedorCnpj(value.cnpj || "");
    setCompraForm((prev) => ({
      ...prev,
      fornecedor: {
        id: value.id,
        nome: value.descricao,
        cnpj: value.cnpj || "",
      },
    }));
  };

  const fecharModalFornecedor = () => {
    setOpenModalFornecedor(false);
    setModalFornecedorEdicaoId(null);
    setModalFornecedorDescricao("");
    setModalFornecedorCnpj("");
  };

  const abrirModalCadastrarFornecedor = () => {
    setModalFornecedorEdicaoId(null);
    setModalFornecedorDescricao("");
    setModalFornecedorCnpj("");
    setOpenModalFornecedor(true);
  };

  const abrirModalEditarFornecedor = () => {
    const sel = fornecedorSelecionadoCombo;
    if (!sel?.id) {
      contextApi.showMessage(
        "Selecione um fornecedor da lista para editar.",
        true,
        "warning"
      );
      return;
    }
    setModalFornecedorEdicaoId(sel.id);
    setModalFornecedorDescricao(sel.descricao);
    setModalFornecedorCnpj(sel.cnpj || "");
    setOpenModalFornecedor(true);
  };

  const salvarFornecedorModal = async () => {
    const descricaoSalvar = modalFornecedorDescricao.trim();
    const cnpjSalvar = modalFornecedorCnpj.trim();
    if (!descricaoSalvar) {
      contextApi.showMessage(
        "Informe o nome ou a razão social do fornecedor.",
        true,
        "warning"
      );
      return;
    }
    const editandoId =
      modalFornecedorEdicaoId != null && modalFornecedorEdicaoId > 0
        ? modalFornecedorEdicaoId
        : null;

    setLoadingFornecedor(true);
    try {
      if (editandoId != null) {
        const res = await comprasService.atualizarFornecedor(editandoId, {
          id: editandoId,
          nome: descricaoSalvar,
          cnpj: cnpjSalvar,
        });
        if ([200, 201, 204].includes(res.status)) {
          contextApi.showMessage(
            "Fornecedor atualizado com sucesso!",
            true,
            "success"
          );
          fecharModalFornecedor();
          await carregarFornecedores();
          const atualizado: FornecedorApi = {
            id: editandoId,
            descricao: descricaoSalvar,
            cnpj: cnpjSalvar,
          };
          aplicarFornecedorSelecionado(atualizado);
        }
        return;
      }

      const res = await comprasService.adicionarFornecedor({
        id: 0,
        nome: descricaoSalvar,
        cnpj: cnpjSalvar,
      });
      if ([200, 201, 204].includes(res.status)) {
        contextApi.showMessage(
          "Fornecedor cadastrado com sucesso!",
          true,
          "success"
        );
        fecharModalFornecedor();
        const raw = res.data as
          | { id?: number; descricao?: string; nome?: string; cnpj?: string }
          | undefined;
        if (raw && typeof raw.id === "number") {
          const normalized = normalizeFornecedorApiItem({
            id: raw.id,
            descricao: raw.descricao,
            nome: raw.nome,
            cnpj: raw.cnpj,
          });
          if (normalized.descricao) {
            await carregarFornecedores();
            aplicarFornecedorSelecionado(normalized);
          } else {
            const lista = await carregarFornecedores();
            const encontrado =
              lista.find(
                (f) =>
                  f.descricao.trim().toLowerCase() ===
                    descricaoSalvar.toLowerCase() &&
                  (f.cnpj || "").trim() === cnpjSalvar
              ) || lista[lista.length - 1];
            if (encontrado) aplicarFornecedorSelecionado(encontrado);
          }
        } else {
          const lista = await carregarFornecedores();
          const encontrado =
            lista.find(
              (f) =>
                f.descricao.trim().toLowerCase() ===
                  descricaoSalvar.toLowerCase() &&
                (f.cnpj || "").trim() === cnpjSalvar
            ) || lista[lista.length - 1];
          if (encontrado) aplicarFornecedorSelecionado(encontrado);
        }
      }
    } catch (error: any) {
      contextApi.showMessage(
        extractErrorMessage(error) ||
          (editandoId != null
            ? "Erro ao atualizar fornecedor."
            : "Erro ao cadastrar fornecedor."),
        true,
        "error"
      );
    } finally {
      setLoadingFornecedor(false);
    }
  };

  const limparFormulario = () => {
    setCompraForm({ ...initialCompra });
    setCompraItens([]);
    setProdutoSelecionado(null);
    setQuantidadeItem(1);
    setValorUnitarioItem(0);
    setFornecedorNome("");
    setFornecedorCnpj("");
  };

  const adicionarItemLocal = () => {
    if (!compraForm.fornecedor?.nome?.trim()) {
      contextApi.showMessage(
        "Selecione um fornecedor antes de incluir produtos.",
        true,
        "warning"
      );
      return;
    }
    if (!produtoSelecionado) {
      contextApi.showMessage(
        "Selecione um produto para incluir na compra.",
        true,
        "warning"
      );
      return;
    }

    if (quantidadeItem <= 0 || valorUnitarioItem <= 0) {
      contextApi.showMessage(
        "Informe quantidade e valor unitário maiores que zero.",
        true,
        "warning"
      );
      return;
    }

    const novoItem: CompraItem = {
      ...initialCompraItem,
      id: Date.now(),
      compraId: compraForm.id,
      produtoId: produtoSelecionado.id,
      produtoDescricao: getProductLabel(produtoSelecionado),
      quantidade: quantidadeItem,
      valorUnitario: valorUnitarioItem,
      valorTotal: quantidadeItem * valorUnitarioItem,
    };

    setCompraItens((prev) => [...prev, novoItem]);
    setProdutoSelecionado(null);
    setQuantidadeItem(1);
    setValorUnitarioItem(0);
  };

  const removerItemLocal = (itemId: number) => {
    setCompraItens((prev) => prev.filter((item) => item.id !== itemId));
  };

  const salvarCompra = async () => {
    if (!compraForm.fornecedor?.nome || compraForm.fornecedor.nome.trim() === "") {
      contextApi.showMessage(
        "Selecione um fornecedor para registrar a compra.",
        true,
        "warning"
      );
      setTabCompras(0);
      return;
    }

    if (compraItens.length === 0) {
      contextApi.showMessage(
        "Inclua pelo menos um produto na compra.",
        true,
        "warning"
      );
      return;
    }

    // Converter CompraItem[] para ProdutosCompraDTO[]
    // Se o ID for muito grande (timestamp do Date.now()), é um item novo, então usar 0
    const produtosDTO: ProdutosCompraDTO[] = compraItens.map((item) => ({
      id: (item.id && item.id < 1000000) ? item.id : 0, // IDs temporários (Date.now()) são muito grandes, usar 0
      descricao: item.produtoDescricao,
      quantidade: item.quantidade,
      valorUnitario: item.valorUnitario,
      iDProdutoEstoque: item.produtoId,
    }));

    const payload: Compra = {
      id: compraForm.id,
      valorTotal: totalCompraAtual,
      excluido: compraForm.excluido || false,
      fornecedor: {
        id: compraForm.fornecedor?.id || 0,
        nome: compraForm.fornecedor.nome,
        cnpj: compraForm.fornecedor.cnpj || "",
      },
      produtos: produtosDTO,
    };

    setLoadingSalvar(true);
    try {
      const request = payload.id
        ? comprasService.atualizarCompra(payload)
        : comprasService.adicionarCompra(payload);
      const response = await request;
      if ([200, 201, 204].includes(response.status)) {
        contextApi.showMessage(
          `Compra ${payload.id ? "atualizada" : "registrada"} com sucesso!`,
          true,
          "success"
        );
        limparFormulario();
        carregarCompras();
        setTabCompras(1);
      }
    } catch (error: any) {
      contextApi.showMessage(
        extractErrorMessage(error) || "Erro ao salvar a compra.",
        true,
        "error"
      );
    } finally {
      setLoadingSalvar(false);
    }
  };

  const editarCompra = async (compra: Compra) => {
    // Converter ProdutosCompraDTO[] para CompraItem[]
    const itens: CompraItem[] = (compra.produtos || []).map((produto) => ({
      id: produto.id,
      compraId: compra.id,
      produtoId: produto.iDProdutoEstoque,
      produtoDescricao: produto.descricao,
      quantidade: produto.quantidade,
      valorUnitario: produto.valorUnitario,
      valorTotal: produto.quantidade * produto.valorUnitario,
    }));

    setCompraForm(compra);
    setCompraItens(itens);
    setFornecedorNome(compra.fornecedor?.nome || "");
    setFornecedorCnpj(compra.fornecedor?.cnpj || "");
    setTabCompras(0);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const excluirCompra = async (compraId: number) => {
    const confirm = window.confirm("Deseja excluir esta compra?");
    if (!confirm) return;
    try {
      const response = await comprasService.removerCompra(compraId);
      if ([200, 204].includes(response.status)) {
        contextApi.showMessage("Compra excluída com sucesso!", true, "success");
        carregarCompras();
        if (compraForm.id === compraId) {
          limparFormulario();
        }
      }
    } catch (error: any) {
      contextApi.showMessage(
        extractErrorMessage(error) || "Erro ao excluir compra.",
        true,
        "error"
      );
    }
  };

  const abrirModalItens = async (compra: Compra) => {
    // Converter ProdutosCompraDTO[] para CompraItem[]
    const itens: CompraItem[] = (compra.produtos || []).map((produto) => ({
      id: produto.id,
      compraId: compra.id,
      produtoId: produto.iDProdutoEstoque,
      produtoDescricao: produto.descricao,
      quantidade: produto.quantidade,
      valorUnitario: produto.valorUnitario,
      valorTotal: produto.quantidade * produto.valorUnitario,
    }));
    setModalCompra(compra);
    setModalItens(itens);
    setOpenModalItens(true);
  };

  const fecharModalItens = () => {
    setOpenModalItens(false);
    setModalCompra(null);
    setModalItens([]);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const temFornecedor = Boolean(compraForm.fornecedor?.nome?.trim());

  const comprasPaginadas = Array.isArray(compras)
    ? compras.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : [];

  const renderCompraActions = (row: Compra) => (
    <>
      <IconButton
        color="primary"
        size="small"
        aria-label="Editar compra"
        onClick={() => editarCompra(row)}
        title="Editar"
      >
        <EditIcon fontSize="small" />
      </IconButton>
      <IconButton
        color="info"
        size="small"
        aria-label="Ver produtos"
        onClick={() => abrirModalItens(row)}
        title="Ver produtos"
      >
        <Inventory2Icon fontSize="small" />
      </IconButton>
      <IconButton
        color="error"
        size="small"
        aria-label="Excluir compra"
        onClick={() => excluirCompra(row.id)}
        title="Excluir"
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </>
  );

  return (
    <Box sx={{ flexGrow: 1, p: { xs: 1, sm: 2 } }}>
      <Paper sx={{ p: { xs: 1, sm: 2 } }}>
        <Typography
          variant="h5"
          sx={{ fontWeight: 600, color: colors.bluePrimary, mb: 0.5 }}
        >
          Compras
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Registre entradas por fornecedor e acompanhe o histórico em separado.
        </Typography>

        <Tabs
          value={tabCompras}
          onChange={(_, v) => setTabCompras(v)}
          sx={{
            mb: 2,
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTab-root": { textTransform: "none", minHeight: 48 },
          }}
        >
          <Tab
            icon={<PostAddIcon fontSize="small" />}
            iconPosition="start"
            label="Nova compra"
          />
          <Tab
            icon={<ListAltIcon fontSize="small" />}
            iconPosition="start"
            label="Compras registradas"
          />
        </Tabs>

        {tabCompras === 0 && (
          <Grid container spacing={2} alignItems="stretch">
            <Grid item xs={12} md={3}>
              <Paper
                sx={{
                  p: 2,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  boxShadow: 2,
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  color={colors.bluePrimary}
                  sx={{ mb: 1 }}
                >
                  1. Fornecedor
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Escolha um fornecedor da lista ou cadastre um novo.
                </Typography>
                <Autocomplete
                  fullWidth
                  options={fornecedorOptions}
                  value={fornecedorSelecionadoCombo}
                  onChange={(_, v) => aplicarFornecedorSelecionado(v)}
                  getOptionLabel={(o) =>
                    o.descricao + (o.cnpj ? ` — ${o.cnpj}` : "")
                  }
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Fornecedor"
                      size="small"
                      placeholder="Buscar na lista..."
                    />
                  )}
                />
                <Button
                  fullWidth
                  variant="outlined"
                  color="primary"
                  startIcon={<AddBusinessIcon />}
                  onClick={abrirModalCadastrarFornecedor}
                  sx={{ height: "40px", mt: 1 }}
                >
                  Cadastrar fornecedor
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  color="secondary"
                  startIcon={<EditIcon />}
                  onClick={abrirModalEditarFornecedor}
                  disabled={!fornecedorSelecionadoCombo?.id}
                  sx={{ height: "40px", mt: 1 }}
                >
                  Editar fornecedor
                </Button>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 2,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  boxShadow: 2,
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  color={colors.bluePrimary}
                  sx={{ mb: 1 }}
                >
                  2. Produtos
                </Typography>
                {!temFornecedor && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Selecione um fornecedor no primeiro card para incluir produtos.
                  </Alert>
                )}
                <Button
                  fullWidth
                  variant="outlined"
                  color="primary"
                  startIcon={<PostAddIcon />}
                  onClick={() => setOpenModalCadastrarProduto(true)}
                  sx={{ mb: 2 }}
                >
                  Cadastrar produto no estoque
                </Button>
                <Autocomplete
                  fullWidth
                  options={produtos}
                  value={produtoSelecionado}
                  disabled={!temFornecedor}
                  onChange={(_, value) => {
                    setProdutoSelecionado(value);
                    setValorUnitarioItem(value?.valor || 0);
                  }}
                  getOptionLabel={(option) => getProductLabel(option)}
                  renderInput={(params) => (
                    <TextField {...params} label="Produto" size="small" />
                  )}
                  sx={{ mb: 2 }}
                />
                <Grid container spacing={2} alignItems="flex-end" sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Quantidade"
                      type="number"
                      size="small"
                      disabled={!temFornecedor}
                      value={quantidadeItem}
                      onChange={(e) =>
                        setQuantidadeItem(parseInt(e.target.value, 10) || 0)
                      }
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Valor unitário"
                      type="number"
                      size="small"
                      disabled={!temFornecedor}
                      value={valorUnitarioItem}
                      onChange={(e) =>
                        setValorUnitarioItem(parseFloat(e.target.value) || 0)
                      }
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  </Grid>
                </Grid>
                <Box sx={{ mt: "auto" }}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    sx={{ height: "40px" }}
                    disabled={!temFornecedor}
                    onClick={adicionarItemLocal}
                  >
                    Incluir na compra
                  </Button>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={5}>
              <Paper
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  boxShadow: 2,
                  minHeight: { xs: 240, md: 0 },
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    bgcolor: "#f8f9fa",
                    borderBottom: "1px solid #e0e0e0",
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    color={colors.bluePrimary}
                  >
                    3. Itens desta compra
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Conferência antes de salvar
                  </Typography>
                </Box>
                <TableContainer
                  sx={{
                    flex: 1,
                    maxHeight: { xs: 280, md: 360 },
                    overflowX: "auto",
                  }}
                >
                  <Table stickyHeader size="small" sx={{ minWidth: 280 }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            color: colors.bluePrimary,
                            fontSize: "0.75rem",
                          }}
                        >
                          Produto
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            color: colors.bluePrimary,
                            fontSize: "0.75rem",
                          }}
                          align="right"
                        >
                          Qtd.
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            color: colors.bluePrimary,
                            fontSize: "0.75rem",
                          }}
                          align="right"
                        >
                          V. unit.
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            color: colors.bluePrimary,
                            fontSize: "0.75rem",
                          }}
                          align="right"
                        >
                          Total
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            color: colors.bluePrimary,
                            fontSize: "0.75rem",
                          }}
                          align="center"
                          width={48}
                          padding="checkbox"
                        >
                          {""}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {compraItens.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 3, px: 1 }}>
                            <Typography color="text.secondary" variant="caption">
                              Nenhum item. Use o card &quot;Produtos&quot;.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        compraItens.map((item, index) => (
                          <TableRow key={`${item.id}-${index}`}>
                            <TableCell
                              sx={{
                                fontSize: "0.75rem",
                                maxWidth: 120,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                              title={item.produtoDescricao}
                            >
                              {item.produtoDescricao}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: "0.75rem" }}>
                              {item.quantidade}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: "0.75rem" }}>
                              {formatToBRL(item.valorUnitario)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: "0.75rem" }}>
                              {formatToBRL(item.valorUnitario * item.quantidade)}
                            </TableCell>
                            <TableCell align="center" size="small">
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => removerItemLocal(item.id)}
                                aria-label="Remover item"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: "#f8fafc",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1.5 }}
                >
                  Resumo da compra atual
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {temFornecedor
                        ? compraForm.fornecedor!.nome
                        : "Nenhum fornecedor selecionado"}
                    </Typography>
                    {temFornecedor && compraForm.fornecedor?.cnpj ? (
                      <Typography variant="body2" color="text.secondary">
                        CNPJ: {compraForm.fornecedor.cnpj}
                      </Typography>
                    ) : null}
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <Typography variant="caption" color="text.secondary">
                      Itens
                    </Typography>
                    <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                      {compraItens.length}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <Typography variant="caption" color="text.secondary">
                      Total
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ color: colors.bluePrimary, lineHeight: 1.2 }}
                    >
                      {formatToBRL(totalCompraAtual)}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={4}
                    sx={{
                      display: "flex",
                      gap: 1,
                      justifyContent: { xs: "stretch", md: "flex-end" },
                      flexWrap: "wrap",
                    }}
                  >
                    <Button
                      variant="contained"
                      size="medium"
                      startIcon={<ShoppingCartCheckoutIcon />}
                      onClick={salvarCompra}
                      disabled={loadingSalvar}
                      sx={{ flex: { xs: 1, md: "none" }, minWidth: 140 }}
                    >
                      {compraForm.id ? "Atualizar compra" : "Salvar compra"}
                    </Button>
                    <Button
                      variant="outlined"
                      size="medium"
                      onClick={limparFormulario}
                      sx={{ flex: { xs: 1, md: "none" }, minWidth: 100 }}
                    >
                      Limpar
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        )}

        {tabCompras === 1 && (
          <Paper sx={{ width: "100%", boxShadow: 2 }}>
            <Box
              sx={{
                px: { xs: 1.5, sm: 2 },
                py: 1.5,
                bgcolor: "#f8f9fa",
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              <Typography variant="subtitle1" fontWeight={600} color={colors.bluePrimary}>
                Histórico
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Editar, ver itens ou excluir compras já registradas
              </Typography>
            </Box>

            {(!compras || !Array.isArray(compras) || compras.length === 0) ? (
              <Box sx={{ textAlign: "center", py: 4, px: 2 }}>
                <Typography color="text.secondary" variant="body2">
                  Nenhuma compra registrada ainda. Use a aba &quot;Nova
                  compra&quot; para começar.
                </Typography>
              </Box>
            ) : (
              <>
                {/* Mobile: cards */}
                <Box
                  sx={{
                    display: { xs: "flex", md: "none" },
                    flexDirection: "column",
                    gap: 1.5,
                    p: { xs: 1, sm: 2 },
                  }}
                >
                  {comprasPaginadas.map((row) => (
                    <Paper key={row.id} variant="outlined" sx={{ p: 1.5 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          mb: 1,
                          gap: 1,
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle1" fontWeight={700}>
                            Compra #{row.id}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {row.produtos?.length || 0} produto(s)
                          </Typography>
                        </Box>
                        <Typography variant="subtitle1" fontWeight={700} color="primary">
                          {formatToBRL(row.valorTotal)}
                        </Typography>
                      </Box>

                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Fornecedor:</strong> {row.fornecedor?.nome || "—"}
                      </Typography>
                      {row.fornecedor?.cnpj ? (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>CNPJ:</strong> {row.fornecedor.cnpj}
                        </Typography>
                      ) : null}

                      <Box sx={{ mt: 1.5, pt: 1, borderTop: "1px solid #eee" }}>
                        {renderCompraActions(row)}
                      </Box>
                    </Paper>
                  ))}
                </Box>

                {/* Desktop: tabela */}
                <TableContainer
                  sx={{
                    display: { xs: "none", md: "block" },
                    maxHeight: 420,
                    overflowX: "auto",
                  }}
                >
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                        <TableCell sx={{ fontWeight: "bold", color: colors.bluePrimary }}>
                          ID
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold", color: colors.bluePrimary }}>
                          Fornecedor
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold", color: colors.bluePrimary }}>
                          Qtd. produtos
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: "bold", color: colors.bluePrimary }}
                          align="right"
                        >
                          Valor total
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: "bold", color: colors.bluePrimary }}
                          align="center"
                          width={160}
                        >
                          Ações
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {comprasPaginadas.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell>{row.id}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {row.fornecedor?.nome || "—"}
                            </Typography>
                            {row.fornecedor?.cnpj ? (
                              <Typography variant="caption" color="text.secondary">
                                CNPJ {row.fornecedor.cnpj}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>{row.produtos?.length || 0}</TableCell>
                          <TableCell align="right">
                            {formatToBRL(row.valorTotal)}
                          </TableCell>
                          <TableCell align="center">
                            {renderCompraActions(row)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={Array.isArray(compras) ? compras.length : 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                bgcolor: "#f8f9fa",
                borderTop: "1px solid #e0e0e0",
              }}
            />
          </Paper>
        )}
      </Paper>

      <Modal
        open={openModalItens}
        onClose={fecharModalItens}
        aria-labelledby="modal-itens-compra"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95vw", sm: 900 },
            maxWidth: "95vw",
            maxHeight: "90vh",
            overflowY: "auto",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
          }}
        >
          <Typography
            id="modal-itens-compra"
            variant="h6"
            sx={{ mb: 1, color: colors.bluePrimary }}
          >
            Produtos da compra #{modalCompra?.id ?? ""}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: "#666" }}>
            Visualize os produtos desta compra.
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <TableContainer sx={{ maxHeight: 360, overflowX: "auto" }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                  <TableCell sx={{ fontWeight: "bold", color: colors.bluePrimary }}>
                    Produto
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: "bold", color: colors.bluePrimary }}
                    align="right"
                  >
                    Quantidade
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: "bold", color: colors.bluePrimary }}
                    align="right"
                  >
                    Valor Unitário
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: "bold", color: colors.bluePrimary }}
                    align="right"
                  >
                    Total
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modalItens.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      Nenhum produto vinculado a esta compra.
                    </TableCell>
                  </TableRow>
                ) : (
                  modalItens.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.produtoDescricao}</TableCell>
                      <TableCell align="right">{item.quantidade}</TableCell>
                      <TableCell align="right">
                        {formatToBRL(item.valorUnitario)}
                      </TableCell>
                      <TableCell align="right">
                        {formatToBRL(item.valorUnitario * item.quantidade)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button variant="outlined" onClick={fecharModalItens}>
              Fechar
            </Button>
          </Box>
        </Box>
      </Modal>

      <ModalCadastrarProduto
        open={openModalCadastrarProduto}
        onClose={() => setOpenModalCadastrarProduto(false)}
        onProdutoCriado={handleProdutoCadastradoNoEstoque}
      />

      <Modal
        open={openModalFornecedor}
        onClose={() => !loadingFornecedor && fecharModalFornecedor()}
      >
        <Box sx={styleModalFornecedor}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            {modalFornecedorEdicaoId != null && modalFornecedorEdicaoId > 0
              ? "Editar fornecedor"
              : "Cadastrar fornecedor"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {modalFornecedorEdicaoId != null && modalFornecedorEdicaoId > 0
              ? "Altere o nome ou o CNPJ e salve. A lista de compras será atualizada."
              : "Após salvar, o fornecedor passa a aparecer na lista e pode ser selecionado na compra."}
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="Nome ou razão social"
            margin="dense"
            value={modalFornecedorDescricao}
            onChange={(e) => setModalFornecedorDescricao(e.target.value)}
            helperText={
              modalFornecedorEdicaoId != null && modalFornecedorEdicaoId > 0
                ? "Enviado como nome na API de atualização."
                : "Enviado como nome no cadastro (AdicionarFornecedor)."
            }
          />
          <TextField
            fullWidth
            size="small"
            label="CNPJ"
            margin="dense"
            value={modalFornecedorCnpj}
            onChange={(e) => setModalFornecedorCnpj(e.target.value)}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
            <Button
              variant="outlined"
              onClick={fecharModalFornecedor}
              disabled={loadingFornecedor}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={salvarFornecedorModal}
              disabled={loadingFornecedor}
            >
              {modalFornecedorEdicaoId != null && modalFornecedorEdicaoId > 0
                ? "Salvar alterações"
                : "Salvar fornecedor"}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}

